import React from 'react';
import { format } from 'date-fns';
import { Globe, Edit, Trash2, Building2, Search, FlaskConical } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Company } from '@/lib/types';
import { WebSearchStatus } from '@/services/companyWebSearchService';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CompanyCardProps {
  company: Company;
  isAdmin: boolean;
  webSearchStatus: WebSearchStatus;
  onView: (companyId: string) => void;
  onEdit?: (companyId: string) => void;
  onDelete?: (companyId: string, companyName: string) => void;
}

const getStatusColor = (status: string | null | undefined) => {
  switch (status) {
    case 'completed':
      return 'text-green-600';
    case 'processing':
      return 'text-amber-500 animate-spin';
    case 'pending':
      return 'text-amber-500';
    case 'failed':
      return 'text-red-500';
    default:
      return 'text-muted-foreground/40';
  }
};

const getStatusLabel = (status: string | null | undefined) => {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Pending';
    case 'failed':
      return 'Failed';
    default:
      return 'Not started';
  }
};

const CompanyCard: React.FC<CompanyCardProps> = ({
  company,
  isAdmin,
  webSearchStatus,
  onView,
  onEdit,
  onDelete,
}) => {
  const deepResearchStatus = (company as any).deep_research_status as string | null;
  const crawlStatus = (company as any).crawl_status as string | null;

  return (
    <Card 
      className="hover:shadow-md transition-shadow cursor-pointer"
      onClick={() => onView(company.id)}
    >
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Building2 className="h-5 w-5 text-primary flex-shrink-0" />
            <h3 className="font-semibold text-base line-clamp-1">{company.name}</h3>
          </div>
          {company.website && (
            <a
              href={company.website}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-blue-500 hover:text-blue-600 flex-shrink-0"
            >
              <Globe className="h-4 w-4" />
            </a>
          )}
        </div>

        {/* Industry */}
        {company.industry && (
          <Badge variant="outline" className="w-fit">
            {company.industry}
          </Badge>
        )}

        {/* Company Details */}
        <div className="space-y-2 text-sm">
          {company.size && (
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Size:</span>
              <Badge variant="secondary">{company.size}</Badge>
            </div>
          )}

          {company.website && (
            <div className="flex items-center gap-2">
              <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <a
                href={company.website}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-blue-500 hover:underline line-clamp-1"
              >
                {company.website}
              </a>
            </div>
          )}

          {/* Enrichment Status Icons */}
          <TooltipProvider>
            <div className="flex items-center gap-3">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Search className={`h-4 w-4 flex-shrink-0 ${getStatusColor(webSearchStatus)}`} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Web Search: {getStatusLabel(webSearchStatus)}</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <FlaskConical className={`h-4 w-4 flex-shrink-0 ${getStatusColor(deepResearchStatus)}`} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Deep Research: {getStatusLabel(deepResearchStatus)}</p>
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Globe className={`h-4 w-4 flex-shrink-0 ${getStatusColor(crawlStatus)}`} />
                </TooltipTrigger>
                <TooltipContent>
                  <p>Website Crawl: {getStatusLabel(crawlStatus)}</p>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>

          <div className="text-muted-foreground">
            Created: {format(new Date(company.created_at), "PPP")}
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-2 border-t">
          {isAdmin ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit?.(company.id);
                }}
                className="flex-1"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(company.id, company.name);
                }}
                className="flex-1"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onView(company.id);
              }}
              className="w-full"
            >
              View Details
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CompanyCard;
